import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { CheckCircle, Copy, CreditCard, EnvelopeSimple, WhatsappLogo } from "@phosphor-icons/react";

export const ShareDialog = ({ voucher, open, onOpenChange }) => {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/invoices/${voucher.id}/share?origin=${encodeURIComponent(window.location.origin)}`),
        api.get(`/share-log?voucher_id=${voucher.id}`),
      ]);
      setInfo(s.data);
      setLog(l.data);
      setEmail(s.data.party_email || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { if (open && voucher?.id) load(); /* eslint-disable-next-line */ }, [open, voucher?.id]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Clipboard blocked by the browser"); }
  };

  const whatsapp = async () => {
    try { await api.post(`/invoices/${voucher.id}/share-log`, { channel: "whatsapp" }); load(); } catch { /* noop */ }
  };

  const payLink = async () => {
    setBusy("pay");
    try {
      const { data } = await api.post(`/invoices/${voucher.id}/payment-link`, { origin_url: window.location.origin });
      setInfo({ ...info, payment_url: data.checkout_url, payment_status: "pending" });
      toast.success(data.reused ? "Existing payment link reused" : "Stripe payment link created");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const sendEmail = async () => {
    setBusy("email");
    try {
      const { data } = await api.post(`/invoices/${voucher.id}/email`, {
        to: email, note: note || null, origin_url: window.location.origin,
      });
      toast.success(`Emailed to ${data.to}`);
      load();
    } catch (e) { toast.error(apiError(e)); load(); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Share {voucher?.number}</DialogTitle>
        </DialogHeader>
        {!info ? (
          <p className="py-6 text-sm text-muted-foreground">Preparing share links…</p>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 border border-border p-3 text-xs">
              <span className="text-muted-foreground">Public link</span>
              <span className="num flex-1 truncate" data-testid="share-pdf-url">{info.pdf_url}</span>
              <Button size="sm" variant="outline" className="h-7 gap-1" data-testid="copy-pdf-link-btn"
                onClick={() => copy(info.pdf_url, "Invoice link")}><Copy size={13} /> Copy</Button>
              <a href={info.pdf_url} target="_blank" rel="noreferrer" className="text-primary underline" data-testid="open-pdf-link">Open</a>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="panel p-4">
                <p className="flex items-center gap-1.5 font-display text-sm font-bold"><WhatsappLogo size={16} /> WhatsApp</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {info.whatsapp_has_number ? `Opens a chat with ${info.party_phone}` : "No party phone on file — pick a contact in WhatsApp"}
                </p>
                <pre className="mt-2 max-h-28 overflow-y-auto whitespace-pre-wrap border border-border bg-muted/40 p-2 text-[10px] leading-relaxed"
                  data-testid="share-message-preview">{info.message}</pre>
                <Button asChild size="sm" className="mt-3 w-full gap-1.5">
                  <a href={info.whatsapp_url} target="_blank" rel="noreferrer" onClick={whatsapp} data-testid="whatsapp-share-btn">
                    <WhatsappLogo size={14} /> Click to send
                  </a>
                </Button>
              </div>

              <div className="panel p-4">
                <p className="flex items-center gap-1.5 font-display text-sm font-bold"><CreditCard size={16} /> Payment link</p>
                {info.payment_status === "paid" ? (
                  <p className="mt-2 flex items-center gap-1 text-sm text-success" data-testid="payment-paid-badge">
                    <CheckCircle size={14} /> Paid
                  </p>
                ) : info.payment_url ? (
                  <>
                    <p className="num mt-2 break-all text-[10px] text-muted-foreground" data-testid="payment-url">{info.payment_url}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1" data-testid="copy-payment-link-btn"
                        onClick={() => copy(info.payment_url, "Payment link")}><Copy size={13} /> Copy</Button>
                      <Button size="sm" className="flex-1" data-testid="open-payment-link-btn"
                        onClick={() => window.open(info.payment_url, "_blank", "noopener")}>Open checkout</Button>
                    </div>
                    <Button size="sm" variant="ghost" className="mt-2 w-full text-[11px]" disabled
                      data-testid="create-payment-link-btn">Payment link already active</Button>
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Stripe Checkout in test mode — collect {money(voucher?.amount || 0)} online.
                    </p>
                    <Button size="sm" className="mt-3 w-full" onClick={payLink} disabled={busy === "pay"} data-testid="create-payment-link-btn">
                      {busy === "pay" ? "Creating…" : "Generate payment link"}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="panel space-y-2 p-4">
              <p className="flex items-center gap-1.5 font-display text-sm font-bold"><EnvelopeSimple size={16} /> Email with PDF</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">To</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com"
                    data-testid="share-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest">Subject</Label>
                  <Input value={info.email_subject} readOnly className="text-muted-foreground" data-testid="share-email-subject" />
                </div>
              </div>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Optional message to the customer" data-testid="share-email-note" />
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground">Delivered via Resend with the tax invoice PDF attached.</p>
                <Button size="sm" onClick={sendEmail} disabled={!email || busy === "email"} data-testid="send-email-btn">
                  {busy === "email" ? "Sending…" : "Send email"}
                </Button>
              </div>
            </div>

            <div className="border border-border">
              <p className="border-b border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">Share history</p>
              <table className="dense-table w-full" data-testid="share-log-table">
                <tbody>
                  {log.map((r) => (
                    <tr key={r.id}>
                      <td className="capitalize">{r.channel}</td>
                      <td className="text-muted-foreground">{r.to || "—"}</td>
                      <td className="num text-[11px] text-muted-foreground">{new Date(r.at).toLocaleString("en-IN")}</td>
                      <td className={r.status === "sent" ? "text-success" : r.status === "failed" ? "text-destructive" : "text-muted-foreground"}>
                        {r.status.replace(/_/g, " ")}
                      </td>
                    </tr>
                  ))}
                  {log.length === 0 && <tr><td colSpan={4} className="text-muted-foreground">Not shared yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
